/**
 * usePickemSelections - Composed Selection Management Hook
 *
 * This hook composes smaller, focused hooks to manage pickem selections.
 * Dramatically reduced from 261 lines to ~40 lines by splitting responsibilities.
 *
 * Architecture:
 * - useSelectionState: Pure state management
 * - useSelectionSync: Syncs with API data
 * - useSelectionHandlers: Event handlers
 * - useSelectionValidation: Validation logic
 * - useSubmissionPayload: Payload building
 */

import type { PickemDetailResponse } from '@low6-pickem/api-types';
import { useSelectionState } from './selections/useSelectionState';
import { useSelectionSync } from './selections/useSelectionSync';
import { useSelectionHandlers } from './selections/useSelectionHandlers';
import { useSelectionValidation } from './selections/useSelectionValidation';
import { useSubmissionPayload } from './selections/useSubmissionPayload';

// Re-export types for backwards compatibility
export type { SelectionState } from './selections/useSelectionState';
export type { SubmissionPayload } from './selections/useSubmissionPayload';

/**
 * Main hook for managing pickem selections
 *
 * @param pickemDetails - Pickem details from API
 * @returns Selection state and all handler functions
 */
export function usePickemSelections(pickemDetails: PickemDetailResponse | undefined) {
  // State management
  const { selections, setSelections } = useSelectionState();

  // Sync selections with API data
  useSelectionSync(pickemDetails, setSelections);

  // Event handlers
  const handlers = useSelectionHandlers(setSelections, pickemDetails, selections);

  // Validation
  const validation = useSelectionValidation(pickemDetails, selections);

  // Payload builder
  const payload = useSubmissionPayload(pickemDetails, selections);

  return {
    selections,
    ...handlers,
    ...validation,
    ...payload,
  };
}
